import { Component, h, Prop, State, Watch } from '@stencil/core';
import { translations, markets } from './pn-proxio-findprice-translations';
import { ProxioPriceListGroupResponse } from '../pn-proxio-pricegroup/types';
import {
  ProxioPriceListGroupsResponse,
  ProxioProductFindPriceWeight,
  ProxioProductFindPriceSizeCategory,
  ProxioProductFindPriceMarket,
  ProxioFindPricei18n,
} from './pn-proxio-findprice-types';
import { MarketWebContextService } from '../../../globals/MarketWebContextService';
import { weights, sizeCategories } from './data.js';
import { FetchHelper } from '../../../globals/FetchHelper';
import { state } from './pn-proxio-findprice-store';
import S from './img/small-package.svg';
import M from './img/medium-package.svg';
import L from './img/large-package.svg';
import person from './img/postnord-person2.svg';
import clouds from './img/clouds.svg';
import { trash_can } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-proxio-findprice',
  styleUrl: 'pn-proxio-findprice.scss',
})
export class PnProxioFindPrice {
  @Prop({ mutable: true }) endpoint: string = null;
  @Prop({ mutable: true }) language: string = 'da';
  @Prop({ mutable: true }) market: string = 'dk';
  @Prop() showMedium: boolean = false;
  @Prop() showInternational: boolean = false;
  @Prop() maxAbroadKg: number = 0;
  @Prop() weightListName: string = '';

  @Prop() cache: boolean = false;
  @State() filteredItems: ProxioPriceListGroupResponse[] = [];
  @State() weight: ProxioProductFindPriceWeight = { value: 0, name: '' } as ProxioProductFindPriceWeight;
  @State() weightvalue: number = 0;
  @State() countrycodevalue: string = '';
  @State() sizecategory: ProxioProductFindPriceSizeCategory = { value: '', name: '', icon: '', unit: '' } as ProxioProductFindPriceSizeCategory;
  @State() data: ProxioPriceListGroupsResponse = { proxioProducts: [], shipmentCountries: [] } as ProxioPriceListGroupsResponse;

  fetchHelper = new FetchHelper('proxiopricegroups');

  componentWillLoad() {
    this.setInitialValues().then(() => {
      this.init();
    });
  }

  async setInitialValues(href = window.location.href) {
    const marketWebContextService = new MarketWebContextService(href);
    const environment = await marketWebContextService.getEnvironmentName();

    if (!this.endpoint && environment?.indexOf('local') === -1) {
      const market = await marketWebContextService.getMarket();
      this.market = this.market ? this.market : market;
      this.endpoint = await marketWebContextService.getEndpoint(environment, market);
    }

    if (!this.endpoint) {
      this.endpoint = '';
    }

    if (this.countrycodevalue === '') {
      this.countrycodevalue = this.market.toLowerCase();
    }
  }

  init() {
    this.setState();
    this.fetchData().then(() => {
      this.getDataSource();
    });
  }

  async getDataSource() {
    try {
      this.sizecategory = (sizeCategories as ProxioProductFindPriceSizeCategory[]).filter(x => x.value === 'S')[0];
      this.filterItems();
    } catch (e) {
      console.log('Unable to parse data');
    }
  }

  async fetchData() {
    if (!this.market || !this.language) {
      return;
    }
    const endpoint = this.endpoint.lastIndexOf('/') === this.endpoint.length - 1 ? this.endpoint.substring(0, this.endpoint.length - 1) : this.endpoint;
    const fetchUrl = `${endpoint}/api/proxio/price-list-groups?market=${this.market}&language=${this.language}&cached=${this.cache}`;
    //const fetchUrl = `https://localhost:51547/api/proxio/price-list-groups?market=${this.market}&language=${this.language}&cached=${this.cache}`;

    const data = (await this.fetchHelper.fetchJson(
      fetchUrl,
      {
        mode: 'cors',
      },
      false,
      this.onFetchData.bind(this),
    )) as any;

    if (data) {
      this.data = data as ProxioPriceListGroupsResponse;
      this.data.proxioProducts = data.proxioProducts as ProxioPriceListGroupResponse[];
    }
    this.filterItems();
  }

  onFetchData(data) {
    this.data = data;
  }

  @Watch('market')
  @Watch('language')
  setState() {
    this.market = this.market ? this.market : 'dk';
    if (this.market) {
      state.market = markets[this.market] as ProxioProductFindPriceMarket;
    }
    if (this.language && translations[this.language]) {
      state.i18n = translations[this.language] as ProxioFindPricei18n;
    }
  }

  @Watch('sizecategory')
  @Watch('weightvalue')
  @Watch('countrycodevalue')
  filterItems() {
    const sizeItemData = this.data.proxioProducts.filter(item => {
      return item.proxioProduct.productWeightGroupList.some(weightGroup => {
        return weightGroup.item3.includes(this.sizecategory.value);
      });
    });

    if (!this.weight) {
      return;
    }
    const comparisonWeight = this.weight.value;

    let weightFilteredData = sizeItemData?.filter(item => {
      return item.proxioProduct.productWeightGroupList.some(weightGroup => {
        return weightGroup.item1 === comparisonWeight;
      });
    });

    let isInternational = this.showInternational && ((this.countrycodevalue?.trim() ?? '') !== '');

    if (isInternational) {
      weightFilteredData = weightFilteredData?.filter(item =>
        item.proxioProduct.weightPriceCountryList.some(countryItem => countryItem.item3.includes(this.countrycodevalue.toUpperCase())),
      );
    }

    weightFilteredData = this.sortWeightItems(weightFilteredData, isInternational);

    this.filteredItems = weightFilteredData;
  }

  parseFloatFromString(text) {
    return parseFloat(text?.replace(/[^\d\.\,]*/g, '') ?? 100000);
  }

  sortWeightItems(weightFilteredData: ProxioPriceListGroupResponse[], isInternational: boolean) {
    weightFilteredData?.sort((a, b) => {
      let aNumber = this.getSortNumber(a, isInternational);
      let bNumber = this.getSortNumber(b, isInternational);

      if (aNumber && bNumber) {
        return aNumber - bNumber;
      }

      if (aNumber < bNumber) {
        return -1;
      }

      if (aNumber > bNumber) {
        return 1;
      }

      return 0;
    }) ?? [];

    return weightFilteredData;
  }

  getSortNumber(proxioPriceListGroupResponseItem: ProxioPriceListGroupResponse, isInternational: boolean) {
    let index = proxioPriceListGroupResponseItem?.proxioProduct?.productWeightGroupList?.findIndex(weightGroup => weightGroup.item1 === this.weight.value) ?? 0;

    if (index < 0) {
      index = 0;
    }

    let pricevalue = proxioPriceListGroupResponseItem?.proxioProduct?.weightAndPriceList[index]?.item2?.toLowerCase() ?? '';

    if (isInternational) {
      let weightPriceCurrentCountryList = proxioPriceListGroupResponseItem?.proxioProduct?.weightPriceCountryList?.filter(countryItem =>
        countryItem.item3.includes(this.countrycodevalue.toUpperCase()),
      );

      pricevalue = weightPriceCurrentCountryList[index]?.item2?.toLowerCase() ?? '';
    }

    let sortNumber = this.parseFloatFromString(pricevalue);

    return sortNumber;
  }

  resetFilters() {
    this.weight = { value: 0, name: '' } as ProxioProductFindPriceWeight;
    this.sizecategory = (sizeCategories as ProxioProductFindPriceSizeCategory[]).filter(x => x.value === 'S')[0];
    this.weightvalue = 0;
    this.countrycodevalue = this.data?.shipmentCountries[0]?.countryCode ?? this.market;
  }

  getWeights() {
    let weightListName = this.market + this.weightListName;
    let marketWeightsArray = weights[weightListName] ?? { S: [], M: [], L: [] };
    let marketWeights = marketWeightsArray[this.sizecategory.unit];
    let isInternationalWeights = this.maxAbroadKg != 0 && this.countrycodevalue.toLowerCase() !== this.market.toLowerCase();
    marketWeights = isInternationalWeights ? marketWeights.filter(number => number.value / 1000 <= this.maxAbroadKg) : marketWeights;

    return marketWeights;
  }

  handleDropdownChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const countryCode = target.value;
    this.countrycodevalue = countryCode;
  }

  render() {
    return (
      <div class="findprice-form">
        <div class="findprice-form-wrapper">
          <fieldset>
            <legend class="findprice__heading">{state.i18n.sizeheading}</legend>
            <div class="sizecategory">
              <table class="sizeTable">
                <img id="clouds" src={clouds} alt={state.i18n.cloudsImage}></img>
                <tr>
                  <td>
                    <img class="firstCell" src={person} alt={state.i18n.personComparison}></img>
                  </td>
                  <td>
                    <img src={S} alt={state.i18n.smallPackage}></img>
                  </td>
                  {this.showMedium ? (
                    <td>
                      <img src={M} alt={state.i18n.mediumPackage}></img>
                    </td>
                  ) : null}
                  <td>
                    <img src={L} alt={state.i18n.largePackage}></img>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  {sizeCategories.map(sizeCategory =>
                    !this.showMedium && sizeCategory.value === 'M' ? null : (
                      <td>
                        <pn-radio-button
                          id={'findpricecategory-' + sizeCategory.value}
                          radioid={sizeCategory.value}
                          value={sizeCategory.value}
                          name="sizecategory"
                          checked={sizeCategory.value === this.sizecategory.value}
                          onChange={() => {
                            this.sizecategory = (sizeCategories as ProxioProductFindPriceSizeCategory[]).filter(x => x.value === sizeCategory.value)[0];
                            this.weight = { value: 0, name: '' } as ProxioProductFindPriceWeight;
                            this.weightvalue = this.weight.value;
                          }}
                        ></pn-radio-button>
                      </td>
                    ),
                  )}
                </tr>
              </table>
            </div>
          </fieldset>

          <fieldset>
            <legend class="findprice__heading findprice__subheading">{state.i18n.weightheading}</legend>
            {this.sizecategory.value && this.getWeights() ? (
              <div class="weightoption">
                {
                  <div class="weightoption-content">
                    {this.getWeights().map(wght => (
                      <pn-choice-button
                        id={'findpriceweight-' + wght.value}
                        choiceid={this.sizecategory.value + wght.value}
                        name="weight"
                        type="radio"
                        value={!this.weight.value ? 0 : this.getWeights().findIndex(x => x.value === this.weightvalue)}
                        checked={wght.value === this.weight.value}
                        onChange={() => {
                          this.weight = (this.getWeights() as ProxioProductFindPriceWeight[]).filter(x => x.value === wght.value)[0];
                          this.weightvalue = this.weight.value;
                        }}
                      >
                        {wght.name}
                      </pn-choice-button>
                    ))}
                  </div>
                }
              </div>
            ) : null}
          </fieldset>

          {this.showInternational && (this.data?.shipmentCountries ?? false) ? (
            <fieldset>
              <div class="shipmentcountry">
                <legend class="shipmentcountry-header">{state.i18n.countryToSendTo}</legend>
                <div class="shipmentcountry-select-container">
                  <svg class="arrow" width="18" height="10" viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M9.70711 9.70711C9.31658 10.0976 8.68342 10.0976 8.29289 9.70711L0.292892 1.70711C-0.0976315 1.31658 -0.0976315 0.683417 0.292892 0.292893C0.683418 -0.0976315 1.31658 -0.0976315 1.70711 0.292893L9 7.58579L16.2929 0.292893C16.6834 -0.0976315 17.3166 -0.0976315 17.7071 0.292893C18.0976 0.683417 18.0976 1.31658 17.7071 1.70711L9.70711 9.70711Z"
                    ></path>
                  </svg>
                  <select name="shipmentCountry-select" id="proxio-shipmentcountry" onChange={this.handleDropdownChange.bind(this)}>
                    {this.data.shipmentCountries.map(item => (
                      <option selected={item.countryCode.toLowerCase() === this.countrycodevalue.toLowerCase()} value={item.countryCode}>
                        {item.countryName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </fieldset>
          ) : null}

          <div class="findprice__clear-all-filters" role="button">
            <div class="findprice__clear-all-filters__btn" onClick={() => this.resetFilters()}>
              <pn-icon icon={trash_can} color="blue700" />
              <span class="clear-all-filters__text">{state.i18n.clearFilterButton}</span>
            </div>
          </div>
        </div>

        {this.filteredItems && this.filteredItems.length > 0 ? (
          <div class="resultlist">
            {state.i18n.resultlistheading ? <div class="resultHeading">{state.i18n.resultlistheading}</div> : null}
            {this.filteredItems.map((item: ProxioPriceListGroupResponse) => (
              <div>
                <pn-proxio-findprice-result
                  activeweight={this.weight.value}
                  item={item}
                  language={this.language}
                  selected-countrycode={this.countrycodevalue}
                  show-international={this.showInternational}
                  market={this.market}
                ></pn-proxio-findprice-result>
              </div>
            ))}
          </div>
        ) : this.weightvalue !== 0 ? (
          <h4 class="resultHeading">{state.i18n.noResultMessage}</h4>
        ) : null}
      </div>
    );
  }
}
